<template>
  <div>
    <template v-if="useFallback">
      <slot v-if="matches" />
      <slot v-else name="fallback" />
    </template>
    <slot v-else :matches="matches" />
  </div>
</template>

<script lang="ts">
import { Component, Vue, Prop } from 'vue-property-decorator';
import { PropType } from 'vue';

export enum Device {
  Mobile = 'mobile',
  Tablet = 'tablet',
  Desktop = 'desktop'
}

const DeviceMediaQuery = {
  [Device.Mobile]: '(max-width: 480px)',
  [Device.Tablet]: '(min-width: 481px) and (max-width: 768px)',
  [Device.Desktop]: '(min-width: 769px)'
};

@Component
export default class BuilderMatchMedia extends Vue {
  @Prop({ type: String, required: false })
  readonly query?: string;

  @Prop({
    type: String as PropType<Device>,
    required: false,
    validator: (value: string) => {
      return Object.values(Device).some(device => device === value);
    }
  })
  readonly device!: Device;

  private mediaQuery?: MediaQueryList;
  public matches = false;

  private addEventListener() {
    if (this.mediaQuery?.addEventListener) {
      this.mediaQuery.addEventListener('change', this.handleChange);
    } else {
      this.mediaQuery?.addListener(this.handleChange);
    }
  }

  private removeEventListener() {
    if (this.mediaQuery?.removeEventListener) {
      this.mediaQuery.removeEventListener('change', this.handleChange);
    } else {
      this.mediaQuery?.removeListener(this.handleChange);
    }
  }

  created() {
    const query = this.query || DeviceMediaQuery[this.device];

    if (!query) {
      console.error('MatchMedia: you must pass a query prop or a device prop');
      return;
    }

    this.mediaQuery = window.matchMedia(query);
    this.matches = this.mediaQuery.matches;

    this.addEventListener();
  }

  private handleChange(event: MediaQueryListEvent) {
    this.matches = event.matches;
  }

  destroyed() {
    this.removeEventListener();
  }

  public get useFallback() {
    return !!this.$slots?.fallback;
  }
}
</script>
